import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useCustomerView } from "@/context/CustomerViewContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFocusScroll } from "@/lib/navigation";
import { SAMPLE_STATUS, human, pluralizeUnit } from "@/lib/statusLabels";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SampleRequestDialog } from "@/components/SampleRequestDialog";
import { SampleLine } from "@/components/samples/SampleLine";
import { SourcingJourney } from "@/components/sourcing/SourcingLifecycle";
import { toast } from "sonner";
import { Loader2, Plus, FlaskConical, Pencil } from "lucide-react";

const STATUS_BADGE = {
  AWAITING_PAYMENT: "badge-reserved",
  REQUESTED: "badge-reserved",
  PARTIALLY_SENT: "badge-reserved",
  SENT: "badge-available",
  DELIVERED: "badge-available",
  CANCELLED: "badge-neutral",
};

export default function DealerSamples() {
  const [requests, setRequests] = useState(null);
  const [allowance, setAllowance] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const { customerView } = useCustomerView();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = searchParams.get("focus");  // V5-D NAV-006 exact sample request
  const focusHit = !!(focusId && requests && requests.some((r) => r.id === focusId));
  useFocusScroll(focusHit ? `sample-request-${focusId}` : null, focusHit);

  const load = () => {
    api.get(`/samples?customer_view=${customerView ? 1 : 0}`).then((res) => setRequests(res.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    if (!customerView) {
      api.get("/samples/allowance").then((res) => setAllowance(res.data)).catch(() => {});
    } else {
      setAllowance(null);
    }
  };

  useEffect(() => {
    load();
    api.get("/catalog").then((res) => setCatalog(res.data)).catch(() => {});
    api.get("/projects").then((res) => setProjects(res.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerView]);

  // §9 SAMP-AT: reconcile against authoritative Stripe state on return from checkout
  useEffect(() => {
    const outcome = searchParams.get("sample_payment");
    const rid = searchParams.get("request");
    if (!outcome) return;
    setSearchParams({}, { replace: true });
    if (outcome === "success" && rid) {
      api.post(`/samples/${rid}/sync-payment`).then((res) => {
        if (res.data.status === "REQUESTED") toast.success("Payment received — your sample request is confirmed.");
        else toast.info("Payment is being confirmed — the request will update automatically.");
        load();
      }).catch(() => { toast.error("Could not confirm payment status — please try again in a moment."); load(); });
    } else if (outcome === "cancelled") {
      toast.info("Checkout cancelled — no charge was made. The request remains awaiting payment.");
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // §9 SAMP-059..065: dealer pre-ship cancellation — refund truthfully reported
  const handleCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const { data } = await api.post(`/samples/${cancelTarget.id}/cancel`, { reason: "Dealer cancelled before shipment" });
      const refund = data.payment?.refund;
      toast.success(data.custom_committed
        ? "Cancelled — the Custom/Morocco 72-hour window had passed, so the committed sample treatment remains."
        : refund?.stripe_refund_id
          ? `Cancelled — refund ${refund.status} ($${(refund.amount_usd ?? 0).toFixed(2)})`
          : "Sample request cancelled.");
      setCancelTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div data-testid="dealer-samples-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Try before you buy</p>
          <h1 className="mt-3 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Samples</h1>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="new-sample-request-button" className="h-11 bg-terracotta text-white transition-all duration-200 hover:-translate-y-[1px] hover:bg-terracotta-hover">
          <Plus className="mr-2 h-4 w-4" /> New sample request
        </Button>
      </div>

      {allowance && (
        <Card className="mt-8 border-border bg-white" data-testid="allowance-card">
          <CardContent className="flex flex-wrap items-center justify-between gap-4 p-6">
            <p className="text-sm text-warmgrey">
              <span className="font-semibold text-charcoal" data-testid="allowance-remaining">{allowance.free_remaining}</span> of{" "}
              {allowance.allowance} free sample units remaining in {allowance.billing_month}
            </p>
            <p className="text-xs text-muted-foreground">Additional units: ${(allowance.unit_charge ?? 0).toFixed(2)} each</p>
          </CardContent>
        </Card>
      )}

      <SampleRequestDialog open={open} onOpenChange={setOpen} catalog={catalog} projects={projects} onSubmitted={() => { load(); api.get("/projects").then((res) => setProjects(res.data)).catch(() => {}); }} />
      <SampleRequestDialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)} editing={editTarget} catalog={catalog} projects={projects} onSubmitted={() => { setEditTarget(null); load(); }} />

      {!requests ? (
        <Loader2 className="mt-12 h-6 w-6 animate-spin text-terracotta" />
      ) : requests.length === 0 ? (
        <Card className="mt-12 border-border bg-white">
          <CardContent className="flex flex-col items-center p-16 text-center">
            <FlaskConical className="h-8 w-8 text-muted-foreground" />
            <p className="mt-4 text-sm text-muted-foreground" data-testid="samples-empty">No sample requests yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="mt-10 space-y-3">
          {requests.map((r) => (
            <Card key={r.id} id={`sample-request-${r.id}`} className={`border-border bg-white ${r.id === focusId ? "ring-2 ring-terracotta" : ""}`} data-testid={`sample-request-${r.id}`} data-focused={r.id === focusId ? "true" : undefined}>
              <CardContent className="p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="space-y-1">
                      {r.lines.map((l) => (
                        <SampleLine key={l.line_id} line={l} testid={`sample-line-${r.id}-${l.line_id}`}>
                          {(l.shipped_quantity ?? 0) > 0 && (
                            <span className="text-xs text-status-available" data-testid={`sample-shipped-${r.id}-${l.line_id}`}> ({l.shipped_quantity} of {l.quantity} shipped)</span>
                          )}
                          {l.charged_quantity > 0 && !customerView && r.unit_charge != null && (
                            <span className="text-xs text-[#8a6435]"> ({l.free_quantity} free · {l.charged_quantity} × ${(l.unit_price ?? r.unit_charge ?? 0).toFixed(2)})</span>
                          )}
                        </SampleLine>
                      ))}
                    </div>
                    {r.project_name && (
                      <p className="mt-2 text-xs font-medium text-warmgrey" data-testid={`sample-project-${r.id}`}>Project: {r.project_name}</p>
                    )}
                    <p className="mt-2 text-xs text-muted-foreground">
                      {r.is_direct_to_client ? "Direct to client" : "Ship to showroom"} · {r.recipient?.city}{r.recipient?.state ? `, ${r.recipient.state}` : ""}
                      {!r.shipment && r.tracking ? ` · Tracking ${r.tracking}` : ""}
                    </p>
                    {r.shipment && (
                      <div className="mt-2 rounded-md border border-border bg-background p-3 text-xs" data-testid={`sample-shipment-${r.id}`}>
                        <p className="font-medium text-charcoal">
                          Shipped{r.shipment.carrier ? ` — ${r.shipment.carrier}` : ""}{r.shipment.service ? ` ${r.shipment.service}` : ""}
                        </p>
                        {r.shipment.tracking_number && (
                          <p className="mt-0.5 font-mono text-charcoal">
                            {r.shipment.shipment_type === "freight" ? "PRO" : "Tracking"}: {r.shipment.tracking_number}
                            {r.shipment.pieces != null ? ` · ${r.shipment.pieces} ${r.shipment.shipment_type === "freight" ? "pallet(s)" : "package(s)"}` : ""}
                          </p>
                        )}
                        {r.shipment.estimated_delivery && (
                          <p className="mt-0.5 text-muted-foreground">Estimated delivery: {new Date(r.shipment.estimated_delivery + "T00:00:00").toLocaleDateString()}</p>
                        )}
                        {r.shipment.tracking_url && (
                          <a href={r.shipment.tracking_url} target="_blank" rel="noopener noreferrer" data-testid={`track-shipment-${r.id}`} className="mt-1 inline-block font-semibold text-terracotta hover:text-terracotta-hover">Track shipment →</a>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {!customerView && r.charge_total > 0 && (
                      <span className={`text-sm font-medium ${r.sample_charge_waived ? "text-warmgrey" : "text-charcoal"}`} data-testid={`sample-charge-${r.id}`}>
                        ${(r.charge_total ?? 0).toFixed(2)}{r.sample_charge_waived && <span className="ml-1 text-xs text-status-available">— Waived</span>}
                      </span>
                    )}
                    {!customerView && (r.shipping_charge_usd != null || r.shipping_waived) && (
                      <span className="text-xs text-warmgrey" data-testid={`sample-shipping-charge-${r.id}`}>
                        Shipping {r.shipping_charge_usd != null ? `$${(r.shipping_charge_usd ?? 0).toFixed(2)}` : ""}{r.shipping_waived ? " — Waived" : ""}
                      </span>
                    )}
                    <span className={`badge-status ${STATUS_BADGE[r.status]}`} data-testid={`sample-status-${r.id}`}>{human(SAMPLE_STATUS, r.status)}</span>
                    {!customerView && r.sourcing_status_label && (
                      <span className={`badge-status ${r.sourcing_status === "UNABLE_TO_SOURCE" ? "badge-sold" : "badge-committed"}`} data-testid={`sample-sourcing-status-${r.id}`}>
                        Sourcing: {r.sourcing_status_label}
                      </span>
                    )}
                  </div>
                  {!customerView && r.needs_sourcing && (
                    <p className="mt-2 text-xs text-[#8a6435]" data-testid={`sample-sourcing-info-${r.id}`}>
                      {r.sourcing_note || "Zelligio is sourcing this sample for you."}
                      {r.sourcing_expected_at
                        ? ` Expected: ${new Date(r.sourcing_expected_at + "T00:00:00").toLocaleDateString()}`
                        : " Timing requires confirmation — Zelligio will verify and update you."}
                    </p>
                  )}
                  {!customerView && r.sourcing_journey && <SourcingJourney journey={r.sourcing_journey} id={`sample-${r.id}`} />}
                  {!customerView && r.sourcing_explanation && <p className="mt-1 text-xs text-charcoal" data-testid={`sample-sourcing-explain-${r.id}`}>{r.sourcing_explanation}</p>}
                  {r.custom_72h && !["CANCELLED", "DELIVERED"].includes(r.status) && (
                    <p className="mt-2 text-xs font-medium text-[#8a6435]" data-testid={`sample-custom-${r.id}`}>
                      Custom/Morocco sample — {r.custom_committed
                        ? "commercially committed (72-hour window passed)"
                        : `cancellable until ${new Date(r.custom_commitment_deadline).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}`}
                    </p>
                  )}
                  {!customerView && r.status === "QUOTE_SENT" && r.quote && (
                    <div className="mt-3 rounded-md border border-terracotta/30 bg-sand/60 p-3 text-xs" data-testid={`sample-quote-${r.id}`}>
                      <p className="font-semibold text-charcoal">Quote from Zelligio — review and pay, or decline</p>
                      <ul className="mt-2 space-y-1">
                        {(r.quote.lines || []).map((l) => (
                          <li key={l.kind} className="flex flex-wrap items-baseline justify-between gap-2" data-testid={`quote-line-${r.id}-${l.kind}`}>
                            <span className="text-charcoal">{l.label}</span>
                            <span className="text-charcoal">
                              {l.waived > 0 ? (
                                <>
                                  <span className="text-warmgrey line-through">${l.original.toFixed(2)}</span>
                                  <span className="ml-1 text-status-available">waived{l.waiver_reason ? ` — ${l.waiver_reason}` : ""}</span>
                                  <span className="ml-1 font-semibold">$0.00</span>
                                </>
                              ) : (
                                <span className="font-semibold">${l.final.toFixed(2)}</span>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                      <p className="mt-2 border-t border-border pt-2 text-sm font-semibold text-charcoal" data-testid={`quote-total-${r.id}`}>
                        Total due: ${(r.quote.total ?? 0).toFixed(2)}
                      </p>
                      <div className="mt-2 flex gap-2">
                        <Button size="sm" onClick={async () => {
                          try {
                            const { data } = await api.post(`/samples/${r.id}/quote/accept`);
                            if (data.status === "AWAITING_PAYMENT" && data.payment?.checkout_url) {
                              toast.info("Quote accepted — redirecting to secure checkout (TEST)…");
                              window.location.assign(data.payment.checkout_url);
                              return;
                            }
                            toast.success("Quote accepted — paid, ready to fulfill. Zelligio has been notified.");
                            load();
                          } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
                        }} data-testid={`quote-accept-${r.id}`} className="bg-terracotta text-white hover:bg-terracotta-hover">
                          Accept{(r.quote.total ?? 0) > 0 ? ` & pay $${(r.quote.total ?? 0).toFixed(2)}` : ""}
                        </Button>
                        <Button size="sm" variant="outline" onClick={async () => {
                          try {
                            await api.post(`/samples/${r.id}/quote/decline`);
                            toast.success("Quote declined — the request is cancelled.");
                            load();
                          } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
                        }} data-testid={`quote-decline-${r.id}`} className="border-limestone text-warmgrey hover:text-charcoal">
                          Decline
                        </Button>
                      </div>
                    </div>
                  )}
                  {!customerView && r.status === "AWAITING_PAYMENT" && r.payment?.checkout_url && (
                    <div className="mt-3">
                      <Button size="sm" onClick={() => window.location.assign(r.payment.checkout_url)} data-testid={`sample-pay-${r.id}`} className="bg-terracotta text-white hover:bg-terracotta-hover">
                        Complete payment — ${(r.payment.amount_usd ?? 0).toFixed(2)}
                      </Button>
                    </div>
                  )}
                  {!customerView && ["AWAITING_PAYMENT", "REQUESTED", "QUOTE_SENT"].includes(r.status) && (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {r.can_edit && (
                        <Button size="sm" variant="outline" onClick={() => setEditTarget(r)} data-testid={`sample-edit-${r.id}`} className="h-10 border-terracotta text-terracotta hover:bg-sand">
                          <Pencil className="mr-1 h-3.5 w-3.5" /> Edit request
                        </Button>
                      )}
                      {["AWAITING_PAYMENT", "REQUESTED"].includes(r.status) && (
                        <Button size="sm" variant="outline" onClick={() => setCancelTarget(r)} data-testid={`sample-cancel-${r.id}`} className="h-10 border-limestone text-warmgrey hover:text-charcoal">
                          Cancel request
                        </Button>
                      )}
                      {r.editable_until && (
                        <span className="text-xs text-muted-foreground" data-testid={`sample-edit-window-${r.id}`}>
                          {r.can_edit ? `Editable until ${new Date(r.editable_until).toLocaleString()}` : "Self-service editing closed (24h) — contact Zelligio for changes"}
                        </span>
                      )}
                    </div>
                  )}
                  {r.status === "DELIVERED" && !customerView && r.lines?.[0]?.variant_id && (
                    <div className="mt-3 flex flex-wrap gap-2 border-t border-border pt-3" data-testid={`sample-next-actions-${r.id}`}>
                      <Button size="sm" variant="outline" onClick={() => navigate(`/dealer/discover/${r.lines[0].variant_id}`)} data-testid={`sample-view-product-${r.id}`} className="h-10 border-limestone text-charcoal hover:bg-sand">View product</Button>
                      <Button size="sm" onClick={() => navigate(`/dealer/discover/${r.lines[0].variant_id}`)} data-testid={`sample-order-${r.id}`} className="h-10 bg-terracotta text-white hover:bg-terracotta-hover">Reserve or order</Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <DialogContent className="border-border bg-white sm:max-w-md" data-testid="sample-cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">Cancel this sample request?</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">
              {cancelTarget?.custom_72h
                ? "Custom/Morocco samples can be cancelled freely within 72 hours of confirmation. After that window the sample is commercially committed — cancellation stops shipment where possible, but charges and allowance treatment remain."
                : "Standard requests can be cancelled any time before shipment. Any collected payment is refunded through the original card."}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex gap-3">
            <Button variant="outline" onClick={() => setCancelTarget(null)} data-testid="sample-cancel-keep" className="flex-1 border-limestone text-charcoal hover:bg-sand">Keep request</Button>
            <Button onClick={handleCancel} disabled={cancelling} data-testid="sample-cancel-confirm" className="flex-1 bg-status-sold text-white hover:opacity-90">
              {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : "Cancel request"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
